'use client'

import { useState } from 'react'
import Image, { type ImageProps } from 'next/image'
import { LuImage } from 'react-icons/lu'
import { useHydrated } from '@/core/hooks/use-hydrated'
import { cn } from '@/core/utils/common'

type SafeImageProps = ImageProps & {
  containerClassName?: string
  /** 원본을 못 불러오면 한 번 바꿔 시도할 이미지(예: 장면 컷 → 썸네일). 이것도 실패하면 아이콘 폴백 */
  fallbackSrc?: string
  /**
   * reveal = 불러오는 동안 스켈레톤, 다 받으면 위에서 아래로 드러난다.
   * plain = 효과 없이 바로 보인다(기본).
   */
  appearance?: 'reveal' | 'plain'
}

/** src 가 바뀌면 key 로 에러·로딩 상태를 처음부터 다시 잡는다 */
export default function SafeImage(props: SafeImageProps) {
  return <SafeImageContent key={getSourceKey(props.src)} {...props} />
}

function getSourceKey(src: ImageProps['src']): string {
  if (typeof src === 'string') return src
  return 'default' in src ? src.default.src : src.src
}

function SafeImageContent({
  className,
  containerClassName,
  alt,
  onLoad,
  onError,
  fill,
  src,
  fallbackSrc,
  appearance = 'plain',
  ...props
}: SafeImageProps) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading'
  )
  const [useFallback, setUseFallback] = useState(false)
  const hydrated = useHydrated()
  const reveal = appearance === 'reveal' && hydrated
  const hasError = status === 'error'
  // src 가 비어 있으면(이미지 없는 데이터) 바로 폴백으로, 둘 다 없으면 아무것도 그리지 않는다
  const current = (useFallback || !src) && fallbackSrc ? fallbackSrc : src
  if (!current) return null

  return (
    <div
      className={cn(
        'overflow-hidden',
        // fill 이면 부모(position 있는 상자)를 꽉 채운다 — relative 와 함께 두면 inset 이 먹지 않는다
        fill ? 'absolute inset-0' : 'relative inline-block',
        containerClassName
      )}
    >
      {/* 스켈레톤 */}
      {reveal && status === 'loading' && (
        <div className="absolute inset-0 z-[1] animate-pulse bg-foreground/10" />
      )}

      {/* 에러 폴백 */}
      {hasError && (
        <div
          role={alt ? 'img' : undefined}
          aria-label={alt || undefined}
          aria-hidden={alt ? undefined : true}
          className="absolute inset-0 flex items-center justify-center bg-foreground/5 text-description"
        >
          <LuImage aria-hidden="true" className="w-8 h-8" />
        </div>
      )}

      {/* 실패해도 치수를 유지하도록 이미지를 지우지 않고 invisible 로 둔다 */}
      <Image
        {...props}
        src={current}
        alt={hasError ? '' : alt}
        aria-hidden={hasError || undefined}
        fill={fill}
        className={cn(
          reveal && 'transition-[clip-path] duration-700 ease-out',
          reveal &&
            (status === 'loading'
              ? '[clip-path:inset(0_0_100%_0)]'
              : '[clip-path:inset(0)]'),
          className,
          hasError && 'invisible'
        )}
        onLoad={(e) => {
          setStatus('loaded')
          onLoad?.(e)
        }}
        onError={(e) => {
          if (fallbackSrc && current !== fallbackSrc) {
            setUseFallback(true)
            return
          }
          setStatus('error')
          onError?.(e)
        }}
      />
    </div>
  )
}
